import { AppStore } from "@/store";
import { toastModule } from "@/modules/toast";
import { uuidModule } from "@/modules/uuid";
import { SessionContextMention } from "@/mds";
import { INoteObservable } from "@/store/note";
import { STEP_IDS } from "@/store/getting-started";
import { TrackedEvent, trackEvent } from "@/domains/metrics";

export const loadOrganizableNotes = async ({
  noteIds,
  store,
}: {
  noteIds: string[];
  store: AppStore;
}): Promise<INoteObservable[] | null> => {
  const notes: INoteObservable[] = [];

  for (const noteId of noteIds) {
    const note = await store.notes.getAsync(noteId);

    if (note) {
      notes.push(note);
    }
  }

  if (notes.length === 0) {
    toastModule.triggerToast({
      content: <div>Unable to find selected notes.</div>,
    });

    return null;
  }

  return notes;
};

export const autoOrganizeNotes = async ({
  noteIds,
  store,
}: {
  noteIds: string[];
  store: AppStore;
}) => {
  const notes = await loadOrganizableNotes({ noteIds, store });

  if (!notes) {
    return;
  }

  const sessionId = uuidModule.generate();

  await store.sessions.createSession({
    sessionId,
    status: "INITIAL",
    kind: "GENERIC",
    config: { schema_version: "V2", variant: "STANDARD", enable_agent_digests: true },
  });

  const mentions: SessionContextMention[] = notes.map(note => ({
    kind: "NOTE",
    note_id: note.id,
    note_primary_label: note.title,
  }));

  const requestContent =
    notes.length === 1 ? `Organize this note for me` : `Organize these notes for me`;

  await store.sessions.createUserMessageEvent({
    sessionId,
    content: requestContent,
    context: {
      mentions,
    },
  });

  store.sidePanel.goToMemIt(sessionId);

  // Mark auto-organize step as complete in getting started guide
  store.gettingStarted?.markStepComplete(STEP_IDS.AUTO_ORGANIZE_NOTE);

  trackEvent(TrackedEvent.AutoOrganizeNote, {
    sessionId,
    requestContent,
    context: {
      mentions,
    },
  });
};
